// Broker deal-first intake (MV-01 3.1, Ship 1b).
// startIntake creates (or attaches to) a draft deal + deal-bound session on
// the server FIRST, then the caller opens the returned apply_url with the
// broker JWT appended as a URL FRAGMENT (#bt=...) — fragments never reach a
// server or its logs; the wizard stashes the token and strips the fragment.

import { api } from '../lib/api.js';
import { getToken } from '../lib/auth.js';

export function startIntake({ accountId = null, dealId = null, name = '', nNumber = '' } = {}) {
  return api.post('/api/tracon/intake/start', {
    account_id: accountId || undefined,
    deal_id: dealId || undefined,
    name: name || undefined,
    n_number: nNumber || undefined,
  });
}

// The token the apex wizard is handed (personal-logins, DESIGN-REVIEW M13): never the broker's own
// personal token, which would put an owner token on the apex origin for as long as the tab lives. The
// server answers a personal sign-in with an intake-scoped token (refused on every owner gate) and the
// shared login with today's token. Any failure throws, with ONE exception (review L5-UX-2): a bundle
// newer than the API meets the old server's catch-all 404 'Not found' for the door, and then a
// SHARED-shaped stored token (none of the personal claims, utils/broker-jwt PERSONAL_CLAIMS) is handed
// over exactly as main does; a personal one is refused as server_behind. The decode is never trusted:
// it only narrows the fallback, and a token that does not decode is not shared.
export const HANDOFF_PATH = '/api/tracon/auth/intake-token';
const DOOR_MISSING = Object.freeze({ status: 404, code: 'Not found' }); // server.js catch-all
const PERSONAL_CLAIMS = ['kind', 'uid', 'urole', 'tv', 'name', 'scope'];
const doorMissing = (e) => !!e && e.status === DOOR_MISSING.status && e.code === DOOR_MISSING.code;

export function sharedShaped(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return false;
    const c = JSON.parse(globalThis.atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return !!c && typeof c === 'object' && !PERSONAL_CLAIMS.some((k) => k in c);
  } catch { return false; }
}

export async function intakeHandoffToken() {
  let d;
  try { d = await api.post(HANDOFF_PATH, {}, { expected: [DOOR_MISSING] }); } catch (e) {
    if (doorMissing(e)) {
      const held = getToken();
      if (held && sharedShaped(held)) return held;
      throw Object.assign(new Error('the server has no intake hand-off door yet'), { code: 'server_behind' });
    }
    throw e;
  }
  if (!d || d.success !== true || typeof d.token !== 'string' || !d.token) throw new Error('no intake hand-off token');
  return d.token;
}

export function handoffWords(e) {
  if (e && e.code === 'server_behind') return 'The intake draft is ready, but the server is behind this page (its sign-in update has not deployed yet), so the wizard cannot be opened with your sign-in. Wait for the deploy, then start the intake.';
  return 'The intake draft is ready, but your sign-in could not be handed to the wizard. Press Start intake again.';
}

// apply_url + the hand-off token as a fragment. Kept in one place so the fragment contract
// (#bt=) has a single writer.
export function brokerApplyUrl(applyUrl, token) {
  return token ? `${applyUrl}#bt=${encodeURIComponent(token)}` : applyUrl;
}

// ─── Intake outreach (MV-01 3.4/3.5 Ship 1a endpoints; Ship 1b UI) ─────────

export function getWelcomeSheet(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/welcome-text?_t=${Date.now()}`);
}

export function sendWelcomeText(dealId, { message, phone } = {}) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/welcome-text/send`, {
    message,
    phone: phone || undefined,
  });
}

export function sendResumeLink(dealId, { channel = 'sms', phone, message } = {}) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/send-resume-link`, {
    channel,
    phone: phone || undefined,
    message: message || undefined,
  });
}

export function getIntakeReadiness(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/intake-readiness?_t=${Date.now()}`);
}
